import { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandIcon, OpenInNew as AbrirIcon } from "@mui/icons-material";
import EstadoChip from "../kit/EstadoChip";
import Etiqueta from "../kit/Etiqueta";
import { useExpedientesGuardados } from "../../queries/useExpedientes";
import { FilaEstudiante, filasDeEstudiantes } from "../../utils/expediente";
import { tiempoTranscurrido } from "../../utils/fecha";
import { BOLETIN_URL, expedienteUrl } from "../../utils/ucampus";

/** Salto a UCampus: botón con enlace, en otra pestaña (la ficha no se pierde). */
function BotonUCampus({ href, children }: { href: string; children: string }) {
  return (
    <Button size="small" variant="outlined" startIcon={<AbrirIcon />} component="a" href={href} target="_blank" rel="noreferrer">
      {children}
    </Button>
  );
}

/** Una carrera: su avance y, debajo, lo que todavía le falta. */
function Carrera({ fila, rut, conEnlace }: { fila: FilaEstudiante; rut: number; conEnlace: boolean }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2">{fila.carrera || "Sin carrera"}</Typography>
        {!!fila.estado && <Etiqueta variante="contorno">{fila.estado}</Etiqueta>}
        {!!fila.nota && (
          <Typography variant="caption" color="text.secondary">
            nota {fila.nota}
          </Typography>
        )}
        <EstadoChip
          label={fila.pendientes.length ? `${fila.pendientes.length} pendiente${fila.pendientes.length === 1 ? "" : "s"}` : "Al día"}
          tono={fila.pendientes.length ? "aviso" : "exito"}
          denso
        />
        <Typography variant="caption" color="text.secondary">
          {fila.completos} de {fila.total} trámites
        </Typography>
        {/* El enlace propio solo cuando hay dos expedientes: con uno, el botón de
            arriba ya lleva ahí y repetirlo es ruido. */}
        {conEnlace && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <BotonUCampus href={expedienteUrl(rut, fila.carrCodigo)}>Ver expediente</BotonUCampus>
          </>
        )}
      </Stack>
      {fila.pendientes.length > 0 && (
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
          {fila.pendientes.map((p) => (
            <Typography component="li" variant="body2" key={p.slug}>
              {p.nombre}
              {!!p.resultado && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · {p.resultado}
                </Typography>
              )}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * El expediente de titulación guardado de un estudiante, plegado en su ficha.
 *
 * El trámite —créditos, bienestar, biblioteca, cédula, envío a Casa Central— vive en
 * UCampus, no acá: esto es la última foto que alguien miró (`core/expedientes.py`), y
 * por eso dice cuándo se consultó. Hasta ahora solo se veía desde Estudiantes, por
 * paso y para la cohorte entera; mirando a una persona había que salir a buscarla.
 *
 * Va plegado porque no es lo que se viene a ver: la ficha es de sus procesos, y el
 * expediente es el otro expediente, el administrativo. Con pendientes el resumen lo
 * dice desde el encabezado, para no tener que abrirlo para enterarse.
 *
 * Los dos saltos a UCampus están aunque no haya nada guardado: desde la ficha de
 * alguien, ir a su expediente o a su boletín es la acción, y no depende de que alguien
 * lo haya consultado antes.
 */
export default function ExpedienteGuardado({ rut }: { rut: number }) {
  const { data, isLoading, isError } = useExpedientesGuardados([rut]);
  const filas = useMemo(() => filasDeEstudiantes(data?.resultados ?? []), [data]);
  const conDatos = filas.filter((f) => !f.error);
  const pendientes = conDatos.reduce((n, f) => n + f.pendientes.length, 0);
  const consultado = tiempoTranscurrido(conDatos[0]?.consultadoEn ?? null);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ mb: 3, border: "1px solid", borderColor: "divider", borderRadius: 2, "&::before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pr: 1 }}>
          <Typography variant="subtitle1">Expediente de titulación</Typography>
          {isLoading ? (
            <CircularProgress size={16} />
          ) : conDatos.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              sin consultar
            </Typography>
          ) : (
            <>
              <EstadoChip
                label={pendientes ? `${pendientes} pendiente${pendientes === 1 ? "" : "s"}` : "Al día"}
                tono={pendientes ? "aviso" : "exito"}
                denso
              />
              {!!consultado && (
                <Typography variant="caption" color="text.secondary">
                  consultado {consultado}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <BotonUCampus href={BOLETIN_URL}>Ver boletín</BotonUCampus>
            {/* Con un solo expediente el enlace lleva derecho a esa carrera; con dos
                —o con ninguno guardado— va sin ella y UCampus pregunta cuál. */}
            <BotonUCampus href={expedienteUrl(rut, conDatos.length === 1 ? conDatos[0].carrCodigo : null)}>
              Ver expediente
            </BotonUCampus>
          </Stack>
          {isError && <Alert severity="error">No se pudo leer el expediente guardado.</Alert>}
          {!isLoading && !isError && conDatos.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Todavía nadie consultó su expediente. Se consulta desde Estudiantes, en la pestaña del paso, con «Revisar
              expediente»; acá se muestra la última foto guardada.
            </Typography>
          )}
          {conDatos.map((f) => (
            <Carrera key={f.clave} fila={f} rut={rut} conEnlace={conDatos.length > 1} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
